/**
 * 1.8.0: the asset page's Rights tab (only while rights management is on): its rights now, the rights form (the
 * model's rights fields), From its sources for an asset PRISM made (each source's rights when it was used and now)
 * and Download rights report (CSV).
 */
import { Download } from 'lucide-react';
import { RIGHTS_STATE_LABELS, rightsColor, rightsFields, type AssetDetail } from '@prism/contracts';
import { useGenealogy, useSchema, rightsReportUrl } from '../api/hooks';
import { rightsInstant, rightsStateText } from '../lib/rights';
import { MetadataTab } from './MetadataTab';
import { MadeFrom } from './GenealogySections';

export function RightsTab({ asset }: { asset: AssetDetail }) {
  const schema = useSchema().data?.definition;
  const ids = schema ? Object.values(rightsFields(schema)).map((f) => f!.id) : [];
  const genealogy = useGenealogy(asset.hasGenealogy === false ? undefined : asset.id);
  const r = asset.rights;
  return (
    <div className="rights-tab" data-testid="rights-tab">
      {r && (
        <div className={`rights-summary ${rightsColor(r)}`} data-testid="rights-summary" data-state={r.state}>
          <strong>© {rightsStateText(r)}</strong>
          <span className="muted">
            {[r.from && `Available from ${rightsInstant(r.from)}`, r.until && `until ${r.until}`, r.holder && `Holder: ${r.holder}`].filter(Boolean).join(' · ') || 'No dates or holder set'}
          </span>
          {r.restrictions && <span>{r.restrictions}</span>}
          {r.credit && <span className="faint">Credit: {r.credit}</span>}
          {r.fromSources && <span className="faint">Includes the sources it was made from ({RIGHTS_STATE_LABELS[r.state].toLowerCase()} is the most restrictive).</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <a className="btn sm" href={rightsReportUrl.asset(asset.id)} download data-testid="rights-report-download"><Download size={13} /> Download rights report (CSV)</a>
      </div>
      {ids.length > 0
        ? <MetadataTab asset={asset} only={ids} />
        : <p className="muted" style={{ margin: 0 }}>The metadata model has no rights fields yet. An administrator adds them in Administration → Rights.</p>}
      {genealogy.data && genealogy.data.madeFrom.length > 0 && (
        <div data-testid="rights-from-sources">
          <p className="section-label">From its sources</p>
          <MadeFrom data={genealogy.data} />
        </div>
      )}
    </div>
  );
}
